import React, { useState } from 'react';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 20px;
  padding: 28px;
  max-width: 440px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const Title = styled.h2<{ $type: 'ban' | 'unban' }>`
  font-size: 18px;
  font-weight: 700;
  color: ${p => p.$type === 'ban' ? '#dc2626' : '#16a34a'};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #94a3b8;
  font-size: 20px;
  padding: 4px;
  line-height: 1;
  
  &:hover {
    color: #64748b;
  }
`;

const UserInfo = styled.div`
  background: #f8fafc;
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Avatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
`;

const UserDetails = styled.div``;

const UserName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1a1f2e;
`;

const UserEmail = styled.div`
  font-size: 12px;
  color: #64748b;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 12px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #2DBCAF;
  }
  
  &::placeholder {
    color: #94a3b8;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Button = styled.button<{ $variant?: 'primary' | 'danger' | 'success' }>`
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  ${p => p.$variant === 'danger' ? `
    background: #dc2626;
    color: #fff;
    border: none;
    
    &:hover:not(:disabled) {
      background: #b91c1c;
    }
  ` : p.$variant === 'success' ? `
    background: #16a34a;
    color: #fff;
    border: none;
    
    &:hover:not(:disabled) {
      background: #15803d;
    }
  ` : `
    background: #fff;
    color: #64748b;
    border: 1px solid #e2e8f0;
    
    &:hover:not(:disabled) {
      background: #f8fafc;
    }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.div`
  color: #dc2626;
  font-size: 13px;
  margin-top: 8px;
`;

interface BanModalProps {
  type: 'ban' | 'unban';
  user: {
    _id: string;
    name?: string;
    email: string;
    avatar?: string;
  };
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
}

const BanModal: React.FC<BanModalProps> = ({ type, user, onClose, onConfirm }) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const handleSubmit = async () => {
    if (!reason.trim()) {
      setError('Alasan wajib diisi');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await onConfirm(reason.trim());
      onClose();
    } catch (err: any) {
      setError(err.message || 'Terjadi kesalahan');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <Header>
          <Title $type={type}>
            {type === 'ban' ? '🚫 Ban User' : '✅ Unban User'}
          </Title>
          <CloseBtn onClick={onClose}>×</CloseBtn>
        </Header>
        
        <UserInfo>
          <Avatar 
            src={user.avatar || '/assets/images/default-avatar.png'} 
            alt={user.name || 'User'} 
          />
          <UserDetails>
            <UserName>{user.name || 'No name'}</UserName>
            <UserEmail>{user.email}</UserEmail>
          </UserDetails>
        </UserInfo>
        
        <FormGroup>
          <Label>
            {type === 'ban' ? 'Alasan Ban *' : 'Alasan Unban *'}
          </Label>
          <TextArea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={
              type === 'ban' 
                ? 'Contoh: Spam content, Pelanggaran kebijakan, dll.'
                : 'Contoh: User sudah klarifikasi, Kesalahan ban, dll.'
            }
          />
          {error && <ErrorText>{error}</ErrorText>}
        </FormGroup>
        
        <Actions>
          <Button onClick={onClose} disabled={loading}>
            Batal
          </Button>
          <Button 
            $variant={type === 'ban' ? 'danger' : 'success'}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Loading...' : type === 'ban' ? '🚫 Ban User' : '✅ Unban User'}
          </Button>
        </Actions>
      </Modal>
    </Overlay>
  );
};

export default BanModal;
